import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import uniqueId from 'lodash/uniqueId';

import { StructureOptionType } from '@Pages/RetroSynthesis/StructureOption/constants';
import SvgUseAsButton from '@Pages/RetroSynthesis/ProjectRoute/ProjectRouteTreeView/components/SvgUseAsButton';

// RX-3549 - remove imports once feature switcher is removed
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import { getShoppingCartIcon } from '@Components/Results/structure/availability/ShoppingCartHelper';
import ReactDOMServer from 'react-dom/server';
import ShoppingCartTooltipContent from '@Components/Results/structure/availability/ShoppingCartTooltipContent';
import { isCustomerIconVisible, getCustomerIconMapping } from '@Components/Results/structure/structure-utils';
import StructureToolbarCustomerIconTooltip from '@Components/Results/structure/StructureToolbarCustomerIconTooltip';
import CustomerLibraryBadge from '@Components/Results/structure/CustomerLibraryBadge';
import { Badge } from '@els/els-react--badge';
import NumberFormatter from '@Utils/NumberFormatter';

// Button positioning constants
const BUTTON_Y_OFFSET = 42;
const CUSTOMER_BUTTON_X_OFFSET = 5;
const CUSTOMER_BUTTON_Y_OFFSET = 43.5;
const SHOPPING_CART_X_BASE = 35;
const ZOOM_BUTTON_X_BASE = 70;
const DETAILS_BUTTON_X_BASE = 103;
const OPTIONS_BUTTON_X_BASE = 137;
const SUPPLIER_BADGE_X_OFFSET = 40;
const SUPPLIER_BADGE_Y_OFFSET = 34;
const CUSTOMER_WITH_CART_OFFSET = 5;
const COMPOSITE_CUSTOMER_OFFSET = 15;
const CART_BADGE_TO_ZOOM_OFFSET = 55;
const COMPACT_BUTTON_STEP = 32;
const COMPOSITE_BUTTON_STEP = 30;

// Button size constants
const CUSTOMER_BUTTON_SIZE = 32;
const STANDARD_BUTTON_SIZE = 26;

const getButtonOffset = (showCustomerIcon, shoppingCartVisible, isCompositeCustomerIcon) => {
  if (!showCustomerIcon) return 0;
  if (isCompositeCustomerIcon) return COMPOSITE_CUSTOMER_OFFSET;
  return shoppingCartVisible ? CUSTOMER_WITH_CART_OFFSET : 0;
};

const customerButton = (x, substanceHeight, label, iconSize, iconOffsetX, iconOffsetY, treeLayout = {}) => ({
  ariaLabel: label,
  height: treeLayout.buttonHeight ?? iconSize ?? CUSTOMER_BUTTON_SIZE,
  width: treeLayout.buttonWidth ?? iconSize ?? CUSTOMER_BUTTON_SIZE,
  x: x - (iconOffsetX ?? CUSTOMER_BUTTON_X_OFFSET),
  y: substanceHeight - (treeLayout.buttonYOffset ?? iconOffsetY ?? CUSTOMER_BUTTON_Y_OFFSET),
});

const shoppingCartButton = (offsetX, substanceHeight, icon, title, ariaDescribedby) => ({
  sprite: icon,
  useButton: true,
  ariaLabel: title,
  ariaDescribedby,
  height: STANDARD_BUTTON_SIZE,
  width: STANDARD_BUTTON_SIZE,
  x: SHOPPING_CART_X_BASE + offsetX,
  y: substanceHeight - BUTTON_Y_OFFSET,
});

const zoomButton = (x, substanceHeight) => ({
  sprite: 'ZoomIn',
  useButton: true,
  height: STANDARD_BUTTON_SIZE,
  width: STANDARD_BUTTON_SIZE,
  x,
  y: substanceHeight - BUTTON_Y_OFFSET,
});

const detailsButton = (x, substanceHeight) => ({
  sprite: 'TextFile',
  useButton: true,
  height: STANDARD_BUTTON_SIZE,
  width: STANDARD_BUTTON_SIZE,
  x,
  y: substanceHeight - BUTTON_Y_OFFSET,
});

const synthesisPlannerButton = (x, substanceHeight) => ({
  sprite: 'Ellipsis',
  useButton: true,
  height: STANDARD_BUTTON_SIZE,
  width: STANDARD_BUTTON_SIZE,
  x,
  y: substanceHeight - BUTTON_Y_OFFSET,
});

function StructureButtons({
  substanceHeight,
  shoppingCartVisible,
  onOptionClick,
  isZoomAvailable,
  supplierNumber,
  viewDetailsEnabled,
  optionsEnabled,
  substanceDetails,
  customerInfo,
}) {
  const showCustomerIcon = isCustomerIconVisible(customerInfo);
  const ciX = shoppingCartVisible ? 10 : 40;
  const shoppingCartTooltipLabel = 'Number of Suppliers';
  const shippingDescId = useMemo(() => uniqueId('shopping-cart-shipping-desc-'), []);

  const {
    icon: customerIcon,
    tooltipLabel,
    iconSize: customerIconSize,
    iconOffsetX: customerIconOffsetX,
    iconOffsetY: customerIconOffsetY,
    badge: customerBadge,
    treeLayout: customerTreeLayout,
  } = getCustomerIconMapping(customerInfo) || {};
  const isCompositeCustomerIcon = !!(customerBadge && customerTreeLayout);
  const offsetX = getButtonOffset(showCustomerIcon, shoppingCartVisible, isCompositeCustomerIcon);
  const supplierBadgeVisible = shoppingCartVisible && Auth.isFeatureEnabled(FEATURES.enableDisplaySupplierNumber) && !!supplierNumber;
  const shoppingCartX = SHOPPING_CART_X_BASE + offsetX;
  const trailingButtonStep = isCompositeCustomerIcon && shoppingCartVisible ? COMPOSITE_BUTTON_STEP : COMPACT_BUTTON_STEP;
  const zoomButtonX = supplierBadgeVisible ? shoppingCartX + CART_BADGE_TO_ZOOM_OFFSET : ZOOM_BUTTON_X_BASE + offsetX;
  const detailsButtonX = supplierBadgeVisible ? zoomButtonX + trailingButtonStep : DETAILS_BUTTON_X_BASE + offsetX;
  const optionsButtonX = supplierBadgeVisible ? detailsButtonX + trailingButtonStep : OPTIONS_BUTTON_X_BASE + offsetX;
  const shoppingCartIcon = getShoppingCartIcon(substanceDetails);
  const shoppingCartTitle = shoppingCartIcon === 'RapidDelivery' ? 'Rapid Substance availability' : 'Substance availability';
  const customerIconAriaLabel = `Copy SMILES from ${tooltipLabel}`;

  return (
    <>
      {showCustomerIcon && (
        <SvgUseAsButton
          onClick={(e) =>
            onOptionClick({
              type: StructureOptionType.CUSTOMER_ICON,
              targetRect: e?.currentTarget?.getBoundingClientRect(),
            })
          }
          wrapperClassName="rt-project-route-substance-node--button svg-use-as-button--focus-ring"
          className={`rt-project-route-substance-node--customer-icon ${customerIcon}`}
          tabIndex="0"
          tooltip={ReactDOMServer.renderToString(<StructureToolbarCustomerIconTooltip customerLabel={tooltipLabel} />)}
          {...(!isCompositeCustomerIcon && { href: `#icon-${customerIcon}` })}
          {...customerButton(
            ciX,
            substanceHeight,
            customerIconAriaLabel,
            customerIconSize,
            customerIconOffsetX,
            customerIconOffsetY,
            customerTreeLayout
          )}>
          {isCompositeCustomerIcon && (
            <>
              <use
                aria-hidden="true"
                className={`rt-project-route-substance-node--customer-icon ${customerIcon}`}
                href={`#icon-${customerIcon}`}
                height={customerTreeLayout.iconSize}
                width={customerTreeLayout.iconSize}
                x={customerTreeLayout.iconX}
                y={customerTreeLayout.iconY}
              />
              <foreignObject
                aria-hidden="true"
                height={customerTreeLayout.badgeHeight}
                width={customerTreeLayout.badgeWidth}
                x={customerTreeLayout.badgeX}
                y={customerTreeLayout.badgeY}>
                <div xmlns="http://www.w3.org/1999/xhtml">
                  <CustomerLibraryBadge content={customerBadge.content} />
                </div>
              </foreignObject>
            </>
          )}
        </SvgUseAsButton>
      )}
      {shoppingCartVisible && (
        <>
          <SvgUseAsButton
            ariaHaspopup="dialog"
            onClick={(e) =>
              onOptionClick({
                type: StructureOptionType.SHOPPING_CARD,
                targetRect: e?.currentTarget?.getBoundingClientRect(),
              })
            }
            tabIndex="0"
            tooltip={ReactDOMServer.renderToString(
              <ShoppingCartTooltipContent details={substanceDetails} supplierNumber={supplierNumber} id={shippingDescId} />
            )}
            wrapperClassName="rt-project-route-substance-node--button"
            className="rt-project-route-substance-node--purchase-button"
            {...shoppingCartButton(offsetX, substanceHeight, shoppingCartIcon, shoppingCartTitle, shippingDescId)}
          />
          {supplierBadgeVisible && (
            <foreignObject
              aria-hidden="true"
              pointerEvents="none"
              x={shoppingCartX + SUPPLIER_BADGE_X_OFFSET}
              y={substanceHeight - SUPPLIER_BADGE_Y_OFFSET}
              width={26}
              height={20}>
              <Badge
                type="subtle"
                size="base"
                label={shoppingCartTooltipLabel}
                title={shoppingCartTooltipLabel}
                content={NumberFormatter(supplierNumber)}
                overlap="space-1o2"
              />
            </foreignObject>
          )}
        </>
      )}
      {isZoomAvailable && (
        <SvgUseAsButton
          onClick={(e) =>
            onOptionClick({
              type: StructureOptionType.ZOOM,
              targetRect: e?.currentTarget?.getBoundingClientRect(),
            })
          }
          wrapperClassName="rt-project-route-substance-node--button"
          className="rt-project-route-substance-node--zoom"
          tabIndex="0"
          tooltip="Zoom in on structure"
          {...zoomButton(zoomButtonX, substanceHeight)}
        />
      )}
      {viewDetailsEnabled && (
        <SvgUseAsButton
          ariaHaspopup="dialog"
          onClick={(e) =>
            onOptionClick({
              type: StructureOptionType.DETAILS,
              targetRect: e?.currentTarget?.getBoundingClientRect(),
            })
          }
          wrapperClassName="rt-project-route-substance-node--button"
          className="rt-project-route-substance-node--zoom"
          tabIndex="0"
          tooltip="Show Details"
          {...detailsButton(detailsButtonX, substanceHeight)}
        />
      )}
      {optionsEnabled && (
        <SvgUseAsButton
          ariaHaspopup="dialog"
          onClick={(e) =>
            onOptionClick({
              type: StructureOptionType.OPTIONS,
              targetRect: e?.currentTarget?.getBoundingClientRect(),
            })
          }
          wrapperClassName="rt-project-route-substance-node--button"
          className="rt-project-route-substance-node--synthesis-planner"
          tabIndex="0"
          tooltip="Show Options"
          {...synthesisPlannerButton(optionsButtonX, substanceHeight)}
        />
      )}
    </>
  );
}

StructureButtons.defaultProps = {
  onOptionClick: () => {},
  isZoomAvailable: true,
  customerInfo: {},
};

StructureButtons.propTypes = {
  substanceHeight: PropTypes.number,
  supplierNumber: PropTypes.number,
  shoppingCartVisible: PropTypes.bool,
  onOptionClick: PropTypes.func,
  isZoomAvailable: PropTypes.bool,
  optionsEnabled: PropTypes.bool,
  viewDetailsEnabled: PropTypes.bool,
  substanceDetails: PropTypes.object,
  customerInfo: PropTypes.object,
};

export default StructureButtons;
